import { supabase } from '../lib/supabase'

import { comercialGestaoService, BROKER_STATUS_ATIVO } from './comercialGestao'

import type {
  PortaisAtendimentoBrokerDetail,
  PortaisAtendimentoBrokerDetailResult,
  PortaisAtendimentoBrokerRow,
  PortaisAtendimentoBrokerSearchFilters,
  PortaisAtendimentoCallQueueEntry,
  PortaisAtendimentoCallQueueItem,
  HelpdeskParameter,
} from '../types/portaisAtendimento'
import {
  PORTAIS_ATENDIMENTO_FILA_LIGACAO_CATEGORIAS_KEY,
  PORTAIS_ATENDIMENTO_FILA_LIGACAO_PRODUCAO_MINIMA_KEY,
  PORTAIS_ATENDIMENTO_PERIODO_DISPONIVEL_COTACAO_KEY,
  PORTAIS_ATENDIMENTO_OUTROS_PARAMETER_KEYS,
  parseFilaLigacaoCategorias,
  parseFilaLigacaoProducaoMinima,
  parsePeriodoDisponivelCotacao,
  serializeFilaLigacaoCategorias,
  serializeFilaLigacaoProducaoMinima,
  serializePeriodoDisponivelCotacao,
} from '../types/portaisAtendimento'



const SUPABASE_PAGE_SIZE = 1000



const LIST_SELECT =

  'id, name, classification, classification_previous, state, city, last_access_date, last_sale_date, id_scgp'



async function fetchAllPages<T>(buildQuery: (from: number, to: number) => any): Promise<T[]> {

  const all: T[] = []

  let from = 0

  let hasMore = true



  while (hasMore) {

    const to = from + SUPABASE_PAGE_SIZE - 1

    const { data, error } = await buildQuery(from, to)



    if (error) throw error



    const page = (data || []) as T[]

    all.push(...page)



    if (page.length < SUPABASE_PAGE_SIZE) {

      hasMore = false

    } else {

      from += SUPABASE_PAGE_SIZE

    }

  }



  return all

}



function normalizeFilter(value?: string | null): string {

  return (value || '').trim()

}



export const portaisAtendimentoService = {

  async searchActiveBrokersForQueue(

    filters: PortaisAtendimentoBrokerSearchFilters = {}

  ): Promise<PortaisAtendimentoBrokerRow[]> {

    const code = normalizeFilter(filters.code)

    const name = normalizeFilter(filters.name)

    const classification = normalizeFilter(filters.classification)

    const state = normalizeFilter(filters.state)

    const city = normalizeFilter(filters.city)



    return fetchAllPages<PortaisAtendimentoBrokerRow>((from, to) => {

      let query = supabase

        .from('brokers')

        .select(LIST_SELECT)

        .eq('status', BROKER_STATUS_ATIVO)

        .order('name')

        .range(from, to)



      if (code) {

        query = query.filter('id_scgp::text', 'like', `%${code}%`)

      }

      if (name) {

        query = query.ilike('name', `%${name}%`)

      }

      if (classification) {

        query = query.eq('classification', classification)

      }

      if (state) {

        query = query.eq('state', state)

      }

      if (city) {

        query = query.eq('city', city)

      }



      return query

    })

  },



  async listActiveBrokerStates(): Promise<string[]> {

    const rows = await fetchAllPages<{ state: string | null }>((from, to) =>

      supabase

        .from('brokers')

        .select('state')

        .eq('status', BROKER_STATUS_ATIVO)

        .not('state', 'is', null)

        .order('state')

        .range(from, to)

    )



    return Array.from(

      new Set(rows.map((r) => normalizeFilter(r.state)).filter(Boolean))

    ).sort((a, b) => a.localeCompare(b, 'pt-BR'))

  },



  async listActiveBrokerCities(state?: string): Promise<string[]> {

    const uf = normalizeFilter(state)



    const rows = await fetchAllPages<{ city: string | null }>((from, to) => {

      let query = supabase

        .from('brokers')

        .select('city')

        .eq('status', BROKER_STATUS_ATIVO)

        .not('city', 'is', null)

        .order('city')

        .range(from, to)



      if (uf) {

        query = query.eq('state', uf)

      }



      return query

    })



    return Array.from(

      new Set(rows.map((r) => normalizeFilter(r.city)).filter(Boolean))

    ).sort((a, b) => a.localeCompare(b, 'pt-BR'))

  },



  async listCallQueueEntries(): Promise<PortaisAtendimentoCallQueueEntry[]> {
    return fetchAllPages<PortaisAtendimentoCallQueueEntry>((from, to) =>
      supabase
        .from('helpdesk_broker_call_queue')
        .select('id, broker_id, reason, sort_order, created_at')
        .order('sort_order', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: true })
        .order('broker_id', { ascending: true })
        .range(from, to)
    )
  },

  async listCallQueueBrokerIds(): Promise<Set<string>> {
    const rows = await this.listCallQueueEntries()
    return new Set(rows.map((r) => r.broker_id).filter(Boolean))
  },

  async listCallQueueWithBrokers(): Promise<PortaisAtendimentoCallQueueItem[]> {
    const entries = await this.listCallQueueEntries()
    if (entries.length === 0) return []

    const brokerIds = entries.map((e) => e.broker_id).filter(Boolean)
    const brokers = await fetchAllPages<{
      id: string
      name: string
      classification: string | null
      id_scgp: number | null
    }>((from, to) =>
      supabase
        .from('brokers')
        .select('id, name, classification, id_scgp')
        .eq('status', BROKER_STATUS_ATIVO)
        .in('id', brokerIds)
        .range(from, to)
    )

    const brokerMap = new Map(brokers.map((b) => [b.id, b]))

    return entries
      .map((entry) => {
        const broker = brokerMap.get(entry.broker_id)
        if (!broker) return null
        return {
          ...entry,
          broker_name: broker.name,
          broker_classification: broker.classification,
          broker_id_scgp: broker.id_scgp,
        }
      })
      .filter((item): item is PortaisAtendimentoCallQueueItem => item !== null)
  },

  async removeBrokersFromCallQueue(brokerIds: string[]): Promise<void> {
    const ids = brokerIds.filter(Boolean)
    if (ids.length === 0) return

    const { error } = await supabase
      .from('helpdesk_broker_call_queue')
      .delete()
      .in('broker_id', ids)

    if (error) throw error
  },

  async addBrokersToCallQueue(
    brokerIds: string[],
    reason: string
  ): Promise<{ addedCount: number; skippedCount: number }> {
    const trimmedReason = reason.trim()
    if (!trimmedReason) {
      throw new Error('Informe o motivo da inclusão na fila.')
    }

    const ids = Array.from(new Set(brokerIds.filter(Boolean)))
    if (ids.length === 0) {
      return { addedCount: 0, skippedCount: 0 }
    }

    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('Usuário não autenticado.')
    }

    const existing = await this.listCallQueueBrokerIds()
    const toAdd = ids.filter((id) => !existing.has(id))
    const skippedCount = ids.length - toAdd.length

    if (toAdd.length === 0) {
      return { addedCount: 0, skippedCount }
    }

    const rows = toAdd.map((broker_id) => ({
      broker_id,
      reason: trimmedReason,
      created_by: user.id,
    }))

    const { error } = await supabase.from('helpdesk_broker_call_queue').insert(rows)
    if (error) throw error

    return { addedCount: toAdd.length, skippedCount }
  },

  async getBrokerDetail(brokerId: string): Promise<PortaisAtendimentoBrokerDetailResult | null> {

    const { data, error } = await supabase.from('brokers').select('*').eq('id', brokerId).maybeSingle()



    if (error) throw error

    if (!data) return null



    const contacts = await comercialGestaoService.listBrokerContacts(brokerId)



    return {

      broker: data as PortaisAtendimentoBrokerDetail,

      contacts,

    }

  },

  async getHelpdeskParameterByKey(key: string): Promise<HelpdeskParameter | null> {
    const { data, error } = await supabase
      .from('helpdesk_parameters')
      .select('*')
      .eq('key', key)
      .maybeSingle()

    if (error) throw error
    return data as HelpdeskParameter | null
  },

  async getFilaLigacaoCategorias(): Promise<string[]> {
    const param = await this.getHelpdeskParameterByKey(PORTAIS_ATENDIMENTO_FILA_LIGACAO_CATEGORIAS_KEY)
    return parseFilaLigacaoCategorias(param?.value)
  },

  async saveFilaLigacaoCategorias(categories: string[]): Promise<HelpdeskParameter> {
    const value = serializeFilaLigacaoCategorias(categories)
    const now = new Date().toISOString()

    const { data, error } = await supabase
      .from('helpdesk_parameters')
      .upsert(
        {
          key: PORTAIS_ATENDIMENTO_FILA_LIGACAO_CATEGORIAS_KEY,
          value,
          description: 'Categorias habilitadas na fila automática de ligação',
          updated_at: now,
        },
        { onConflict: 'key' }
      )
      .select()
      .single()

    if (error) throw error
    return data as HelpdeskParameter
  },

  async getFilaLigacaoProducaoMinima(): Promise<number | null> {
    const param = await this.getHelpdeskParameterByKey(PORTAIS_ATENDIMENTO_FILA_LIGACAO_PRODUCAO_MINIMA_KEY)
    return parseFilaLigacaoProducaoMinima(param?.value)
  },

  async saveFilaLigacaoProducaoMinima(value: number | null): Promise<HelpdeskParameter> {
    const serialized = serializeFilaLigacaoProducaoMinima(value)
    const now = new Date().toISOString()

    const { data, error } = await supabase
      .from('helpdesk_parameters')
      .upsert(
        {
          key: PORTAIS_ATENDIMENTO_FILA_LIGACAO_PRODUCAO_MINIMA_KEY,
          value: serialized,
          description: 'Valor mínimo de produção para corretores na fila automática de ligação (vazio = sem filtro)',
          updated_at: now,
        },
        { onConflict: 'key' }
      )
      .select()
      .single()

    if (error) throw error
    return data as HelpdeskParameter
  },

  async getPeriodoDisponivelCotacao(): Promise<number | null> {
    const param = await this.getHelpdeskParameterByKey(PORTAIS_ATENDIMENTO_PERIODO_DISPONIVEL_COTACAO_KEY)
    return parsePeriodoDisponivelCotacao(param?.value)
  },

  async savePeriodoDisponivelCotacao(months: number): Promise<HelpdeskParameter> {
    const value = serializePeriodoDisponivelCotacao(months)
    const now = new Date().toISOString()

    const { data, error } = await supabase
      .from('helpdesk_parameters')
      .upsert(
        {
          key: PORTAIS_ATENDIMENTO_PERIODO_DISPONIVEL_COTACAO_KEY,
          value,
          description:
            'Número de meses anteriores à data atual para o período disponível de cotação',
          updated_at: now,
        },
        { onConflict: 'key' }
      )
      .select()
      .single()

    if (error) throw error
    return data as HelpdeskParameter
  },

  /** Parâmetros da subaba Outros (helpdesk_parameters por keys conhecidas). */
  async listOutrosParameters(): Promise<HelpdeskParameter[]> {
    const keys = [...PORTAIS_ATENDIMENTO_OUTROS_PARAMETER_KEYS]
    if (keys.length === 0) return []

    const { data, error } = await supabase
      .from('helpdesk_parameters')
      .select('*')
      .in('key', keys)
      .order('key')

    if (error) throw error

    const rows = (data || []) as HelpdeskParameter[]
    const byKey = new Map(rows.map((row) => [row.key, row]))

    return keys
      .map((key) => byKey.get(key))
      .filter((row): row is HelpdeskParameter => row != null)
  },

  async isCurrentUserHelpdeskAdmin(): Promise<boolean> {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) return false

    const { data, error } = await supabase
      .from('profiles')
      .select('helpdesk_user_group:helpdesk_user_groups(is_admin, is_active)')
      .eq('id', user.id)
      .maybeSingle()

    if (error) throw error

    const group = data?.helpdesk_user_group as { is_admin?: boolean; is_active?: boolean } | null
    return group?.is_admin === true && group?.is_active === true
  },

}


